import { Auth } from "aws-amplify";
import axios from "axios";

import { cogData } from "./cognito";
import { changeCase } from "./utilities";
import stageConfig from "../stage-config";

// The stage's appback (src/stage-config.js). REACT_APP_APPBACK_URL overrides
// it for a local run; set to "" → same-origin (CRA dev proxy forwards to a
// local appback, avoiding Chrome's Local Network Access block on cross-port
// localhost fetches).
const API_URL = process.env.REACT_APP_APPBACK_URL ?? stageConfig.appbackUrl;
//console.log("API URL: " + API_URL);
const handleError = (status, body) => {
  console.log("Error:", status, body);
};

// The catalog reads the Live Tap renderer's resource adapter makes, parameterised
// on the transport and path prefix: appback's catalog, `/tap/…` signed in and
// `/live/{token}/tap/…` over a live link (TT-1159), serves the same shapes
// through both, so one set of bodies covers both.
const catalogCalls = (exec, base = "") => ({
  // `includeSlots` asks for every layout's slots in this one response
  // (`?include=slots`, TT-1265) instead of a getLayout per layout. An appback
  // that predates it ignores the query and answers without `slots`.
  getLayouts: async ({ includeSlots = false } = {}) => {
    const res = await exec("GET", `${base}/tap/layouts`, includeSlots ? { include: "slots" } : undefined);

    if (res.layouts) {
      res.layouts.forEach((layout) => {
        if (layout?.backgrounds.length) {
          layout.backgrounds = changeCase.toCamelfromSnakeList(layout.backgrounds);
        }
      });
    }

    return res;
  },

  // get layout data, including slots, not including backgrounds
  getLayout: async (layoutID) => {
    const res = await exec("GET", `${base}/tap/layouts/${layoutID}`);
    return changeCase.toCamelfromSnakeArr(res);
  },

  getBackgroundDescription: async (backgroundId) => {
    return exec("GET", `${base}/tap/backgrounds/${backgroundId}/description`);
  },

  // Every symbol in the catalog, keyed by its symbol_id (TT-1159): each
  // `{ symbol_id, name, pos, … }`, its name only what it is called.
  getSymbols: async () => {
    return exec("GET", `${base}/tap/symbols`);
  },

  getMasterSymbols: async () => {
    return exec("GET", `${base}/tap/symbols/master`);
  },

  // A symbol's images, by its symbol_id: a slug, safe in a path as it is.
  getSymbolImages: async (symbolId) => {
    if (!symbolId) {
      return [];
    }
    const res = await exec("GET", `${base}/tap/symbols/${symbolId}/images`);
    return Array.isArray(res) ? res : [];
  },

  // The board or scene renderer's art (TT-1310), or the painted text boxes'
  // (TT-1334): the catalog's selected
  // pieces of that renderer's roles as the manifest it reads, from appback's
  // /tap, the one catalog route set (TT-1159).
  getArtManifest: async (renderer) => {
    return exec("GET", `${base}/tap/art/${renderer}/manifest`);
  },
});

const AppBackService = {
  axiosExcNoAauth: async (type, url, params, data) => {
    try {
      const resp = await axios({
        method: `${type}`,
        url: API_URL + url,
        headers: {},
        data,
        params,
      });
      resp.data =
        resp.data.length && resp.data.map
          ? changeCase.toCamelfromSnakeList(resp.data)
          : changeCase.toCamelfromSnakeArr(resp.data);
      return resp.data;
    } catch (error) {
      const { status, data } = error.response ?? error.message;
      handleError(status, { msg: data?.message });
      return { error, status, data, msg: data?.message };
    }
  },

  axiosExc: async (type, url, params, data) => {
    try {
      const sess = await Auth.currentSession();
      const resp = await axios({
        method: `${type}`,
        url: API_URL + url,
        headers: {
          Authorization: `Bearer ${sess.accessToken.jwtToken}`,
        },
        data,
        params,
      });
      resp.data = Array.isArray(resp.data)
        ? changeCase.toCamelfromSnakeList(resp.data)
        : changeCase.toCamelfromSnakeArr(resp.data);
      return resp.data;
    } catch (error) {
      console.log(url, error);
      const { status, data } = error.response ?? error.message;
      handleError(status, { msg: data?.message });
      return { error, status, data, msg: data?.message };
    }
  },

  /*

  API Keys (TT-801)

  Note: axiosExc's changeCase is shallow — top-level response keys are
  camelized (api_key -> apiKey) but nested key metadata stays snake_case
  (key_prefix, created_at, ...). Consumers read meta fields as snake_case.

  */
  listApiKeys: async (userId) => {
    return AppBackService.axiosExc("GET", `/users/${userId}/api-keys`);
  },

  createApiKey: async (userId, { name, scopes }) => {
    return AppBackService.axiosExc("POST", `/users/${userId}/api-keys`, {}, { name, scopes });
  },

  rotateApiKey: async (userId, keyId) => {
    return AppBackService.axiosExc("POST", `/users/${userId}/api-keys/${keyId}/rotate`);
  },

  revokeApiKey: async (userId, keyId) => {
    return AppBackService.axiosExc("POST", `/users/${userId}/api-keys/${keyId}/revoke`);
  },

  /*

  Auth

  */
  authorize: async (userId, code) => {
    const res = await AppBackService.axiosExc("PUT", `/users/${userId}/zoom/auth/${code}`);
    return res;
  },

  getZoomAuthCode: async (userId) => {
    const res = await AppBackService.axiosExc("GET", `/users/${userId}/zoom/auth-code`);
    return res;
  },

  upsertZoomAuthCode: async (userId, zAuthCode, zoomLinked = false) => {
    console.log("calling addZoomAuthCode with: ", userId, zAuthCode, zoomLinked);
    const res = await AppBackService.axiosExcNoAauth(
      "POST",
      `/users/${userId}/zoom/auth-code`,
      {},
      {
        zoom_auth_code: zAuthCode,
        zoom_linked: zoomLinked,
      }
    );
    return res;
  },

  // TT-1208: the username Zoom's app context belongs to — the first step of
  // signing in from inside Zoom (services/zoomSignIn.js). No bearer: there is
  // no session yet. Never log `context`; it is a credential until it expires.
  zoomAppSignin: async (context) => {
    return AppBackService.axiosExcNoAauth("POST", "/zoom/app-signin", {}, { context });
  },

  refreshZoomAuth: async (userId) => {
    const res = await AppBackService.axiosExc("PUT", `/users/${userId}/zoom/auth/refresh`);
    return res;
  },

  /*

  Users

  */

  insertUser: async (source) => {
    const userData = await cogData();
    const body = {
      user_id: userData.cognitoUserName,
      first_name: userData.cognitoGivenName,
      last_name: userData.cognitoFamilyName,
      email: userData.cognitoUserEmail,
      phonenum: userData.cognitoUserPhoneNumber,
      source: source || "web",
    };
    await AppBackService.axiosExc("POST", "/users", {}, body);
  },

  getUser: async (userId) => {
    const res = await AppBackService.axiosExc("Get", `/users/${userId}`);
    return res;
  },

  // Sync a user's email into appback's DB after the Cognito change is confirmed.
  // Backend: PUT /users/{userId}/email -> { ok: true, email } (TT-957).
  updateUserEmail: async (userId, email) => {
    const res = await AppBackService.axiosExc("PUT", `/users/${userId}/email`, {}, { email });
    return res;
  },

  /*

  Source audio retention (TT-1190, backend TT-1187)

  One switch per source class: { classes: { meeting, youtube, spaces, upload } }.
  PUT takes a partial map and answers with the full one. A class turned off
  applies to docs created after it; audio already kept goes per doc.

  */
  getMediaSettings: async (userId) => {
    return AppBackService.axiosExc("GET", `/users/${userId}/settings/media`);
  },

  updateMediaSettings: async (userId, classes) => {
    return AppBackService.axiosExc("PUT", `/users/${userId}/settings/media`, {}, { classes });
  },

  /*

  Docs

  */

  // List Documents
  getUserDocs: async (userId, queryParams) => {
    const res = await AppBackService.axiosExc("Get", `/users/${userId}/docs`, queryParams);

    if (res.docs) {
      res.docs = changeCase.toCamelfromSnakeList(res.docs);
      res.docs.forEach((doc) => {
        if (doc.taps?.length) {
          doc.taps = changeCase.toCamelfromSnakeList(doc.taps);
        }
      });
    }

    return res;
  },

  getUserDoc: async (userId, docId, queryParams) => {
    const res = await AppBackService.axiosExc("Get", `/users/${userId}/docs/${docId}`, queryParams);

    if (res.docs) {
      res.docs = changeCase.toCamelfromSnakeList(res.docs);
      res.docs.forEach((doc) => {
        if (doc.taps?.length) {
          doc.taps = changeCase.toCamelfromSnakeList(doc.taps);
        }
        if (Array.isArray(doc.content)) {
          doc.content = doc.content.join("");
        }
      });
    }

    return res;
  },

  /*

  Doc Sharing (TT-807, backend TT-693)

  Share rows come back snake_case; camelize the list like getUserDocs does
  so consumers read share.fromUserName, share.docTitle, share.toUserEmail...

  */

  shareDoc: async (userId, docId, { toUserEmail, permission }) => {
    return AppBackService.axiosExc(
      "POST",
      `/users/${userId}/docs/${docId}/share`,
      {},
      { to_user_email: toUserEmail, permission }
    );
  },

  getPendingShares: async (userId) => {
    const res = await AppBackService.axiosExc("GET", `/users/${userId}/shares/pending`);
    if (res.shares) {
      res.shares = changeCase.toCamelfromSnakeList(res.shares);
    }
    return res;
  },

  // Owner view of a doc's pending + accepted shares (manage access / revoke)
  getDocShares: async (userId, docId) => {
    const res = await AppBackService.axiosExc("GET", `/users/${userId}/docs/${docId}/shares`);
    if (res.shares) {
      res.shares = changeCase.toCamelfromSnakeList(res.shares);
    }
    return res;
  },

  acceptShare: async (userId, shareId) => {
    return AppBackService.axiosExc("POST", `/users/${userId}/shares/${shareId}/accept`);
  },

  declineShare: async (userId, shareId) => {
    return AppBackService.axiosExc("POST", `/users/${userId}/shares/${shareId}/decline`);
  },

  revokeDocShare: async (userId, docId, targetUserId) => {
    return AppBackService.axiosExc(
      "POST",
      `/users/${userId}/docs/${docId}/revoke`,
      {},
      { target_user_id: targetUserId }
    );
  },

  // Upload a Document
  uploadDoc: async (userId, data) => {
    const res = await AppBackService.axiosExc("POST", `/users/${userId}/docs`, {}, data);
    return res;
  },

  // Delete a Document
  deleteUserDoc: async (userId, docId) => {
    const res = await AppBackService.axiosExc("DELETE", `/users/${userId}/docs/${docId}`);
    return res;
  },

  // Delete a Document's retained source audio (204); the doc and its tapestries stay.
  deleteDocSourceAudio: async (userId, docId) => {
    return AppBackService.axiosExc("DELETE", `/users/${userId}/docs/${docId}/source-audio`);
  },

  // Update a Document
  updateUserDoc: async (userId, docId, data) => {
    const res = await AppBackService.axiosExc("PUT", `/users/${userId}/docs/${docId}`, {}, data);
    return res;
  },

  retryUserDoc: async (userId, docId) => {
    return AppBackService.updateUserDoc(userId, docId, { retry: true });
  },

  /*

  Live links (TT-1110) — a public, view-only link to a running live tapestry.

  Owner side is Cognito-gated like every doc route. The public side takes no
  session at all: the token in the path is the credential, and every route is
  a read (there is no advance). A revoked or expired link answers 410.

  */
  mintLiveLink: async (userId, docId, { liveSystem, expiresAt } = {}) => {
    return AppBackService.axiosExc(
      "POST",
      `/users/${userId}/docs/${docId}/live-link`,
      {},
      { live_system: liveSystem, expires_at: expiresAt }
    );
  },

  listLiveLinks: async (userId, docId) => {
    const res = await AppBackService.axiosExc("GET", `/users/${userId}/docs/${docId}/live-link`);
    if (Array.isArray(res?.links)) {
      res.links = changeCase.toCamelfromSnakeList(res.links);
    }
    return res;
  },

  revokeLiveLink: async (userId, docId, token) => {
    return AppBackService.axiosExc("DELETE", `/users/${userId}/docs/${docId}/live-link/${token}`);
  },

  getLiveLink: async (token) => {
    return AppBackService.axiosExcNoAauth("GET", `/live/${token}`);
  },

  // The doc read and a cassette's bands through the link (TT-1270): the
  // public view of the owner's reads, with no signed audio, no ids, and only
  // the cassette's own bands (sentences, marks, plans).
  getLiveLinkDoc: async (token) => {
    return AppBackService.axiosExcNoAauth("GET", `/live/${token}/doc`);
  },

  getLiveLinkBands: async (token, csstId, queryParams) => {
    return AppBackService.axiosExcNoAauth("GET", `/live/${token}/cssts/${csstId}/bands`, queryParams);
  },

  getLiveLinkSceneBoard: async (token, since) => {
    return AppBackService.axiosExcNoAauth("GET", `/live/${token}/scene-board`, since == null ? {} : { since });
  },

  // The catalog the renderer needs, gated by the link token instead of a
  // session: the same calls as the signed-in ones above, over
  // /live/{token}/tap, which serves the renderer's reads only.
  liveLinkCatalog: (token) => catalogCalls((...args) => AppBackService.axiosExcNoAauth(...args), `/live/${token}`),

  /*

  Resources

  */

  ...catalogCalls((...args) => AppBackService.axiosExc(...args)),

  // get all backgrounds associated with a background's primary symbol (or layout)
  getRelatedBackgrounds: async (backgroundId) => {
    const res = await AppBackService.axiosExc("GET", `/tap/backgrounds/${backgroundId}/backgrounds`);
    return changeCase.toCamelfromSnakeList(res);
  },

  getMarkups: async () => {
    let res = await AppBackService.axiosExc("GET", `/tap/markups`);
    res = changeCase.toCamelfromSnakeList(res);
    for (const markup of res) {
      const markupS3 = await axios.get(`https://vt-markups.s3.us-west-2.amazonaws.com/${markup.id}.svg`);
      if (markupS3.data) {
        markup.svg = markupS3.data;
      }
    }
    return res;
  },

  /*

  Taps

  */

  getUserTap: async (userId, tapId, queryParams) => {
    //TODO Once we have the paging working, we will want to generalize into a
    // default params object that can be used for all the paging calls
    const defaultParams = {
      perPage: 10,
      page: 1,
      sort: "date",
      order: "desc",
    };

    const res = await AppBackService.axiosExc("Get", `/users/${userId}/taps/${tapId}`, queryParams ?? defaultParams);

    if (res.taps) {
      res.taps = changeCase.toCamelfromSnakeArr(res.taps);
    }

    return res;
  },

  updateUserTap: async (userId, tapId, data) => {
    const res = await AppBackService.axiosExc("PUT", `/users/${userId}/taps/${tapId}`, {}, data);
    return res;
  },

  deleteUserTap: async (userId, tapId) => {
    const res = await AppBackService.axiosExc("DELETE", `/users/${userId}/taps/${tapId}`);
    return res;
  },

  createUserTap: async (userId, data) => {
    const res = await AppBackService.axiosExc("POST", `/users/${userId}/taps`, {}, data);
    return res;
  },

  startFyre: async (userId, data) => {
    const res = await AppBackService.axiosExc("POST", `/users/${userId}/fyre`, {}, data);
    return res;
  },

  // The capture Zoom auto-start may already be running for this meeting
  // (TT-1232): { docId, title }, or {} when nothing runs.
  getRunningFyre: async (userId, meetingUuid) => {
    return AppBackService.axiosExc("GET", `/users/${userId}/fyre/running`, { meeting_uuid: meetingUuid });
  },

  // This meeting's newest tapestry in any status (TT-1207 F1): { docId,
  // title, status, setUp }, or {} when the meeting never had one. setUp:
  // the user has been through the start modal for it (a start, or naming an
  // auto-started one).
  getMeetingTapestry: async (userId, meetingUuid) => {
    return AppBackService.axiosExc("GET", `/users/${userId}/fyre/latest`, { meeting_uuid: meetingUuid });
  },

  // Host-audio lane (TT-1102): the browser captures and transcribes, appback
  // presigns the Transcribe session and relays each drip onto the spine.
  createSttSession: async (userId, docId) => {
    const res = await AppBackService.axiosExc("POST", `/users/${userId}/docs/${docId}/live-transcript/stt-session`);
    return res;
  },

  postDrip: async (userId, docId, drip) => {
    const res = await AppBackService.axiosExc("POST", `/users/${userId}/docs/${docId}/live-transcript/drips`, {}, drip);
    return res;
  },

  // A POST on page unload: axios can't outlive the page, a keepalive fetch
  // can. The caller supplies a bearer it cached while the page was alive.
  // `body`, when given, goes as JSON. Never throws.
  postKeepalive: (path, bearer, body) => {
    const headers = { Authorization: `Bearer ${bearer}` };
    if (body !== undefined) headers["Content-Type"] = "application/json";
    try {
      return fetch(`${API_URL}${path}`, {
        method: "POST",
        keepalive: true,
        headers,
        body: body === undefined ? undefined : JSON.stringify(body),
      }).catch(() => null);
    } catch (e) {
      return Promise.resolve(null);
    }
  },

  postDripKeepalive: (userId, docId, drip, bearer) =>
    AppBackService.postKeepalive(`/users/${userId}/docs/${docId}/live-transcript/drips`, bearer, drip),

  // Retained audio for the host-audio lane (TT-1190, platform TT-1101). One
  // set per capture session, its epoch minted by the server (`segments/
  // session`: {retain, epoch}; {retain:false} when the doc keeps no audio, 429
  // at the doc's session cap); a presigned PUT per 60 s WAV segment n of that
  // epoch (410 once the session's time bound has passed); and the close that
  // hands the epoch's set to retention. The URL signs the exact byte count
  // and content type.
  createSegmentSession: async (userId, docId) => {
    return AppBackService.axiosExc("POST", `/users/${userId}/docs/${docId}/live-transcript/segments/session`);
  },

  createSegmentUrl: async (userId, docId, { epoch, n, bytes }) => {
    return AppBackService.axiosExc("POST", `/users/${userId}/docs/${docId}/live-transcript/segment-url`, {
      epoch,
      n,
      bytes,
    });
  },

  closeSegments: async (userId, docId, epoch) => {
    return AppBackService.axiosExc("POST", `/users/${userId}/docs/${docId}/live-transcript/segments/close`, {
      epoch,
    });
  },

  closeSegmentsKeepalive: (userId, docId, epoch, bearer) =>
    AppBackService.postKeepalive(
      `/users/${userId}/docs/${docId}/live-transcript/segments/close?${new URLSearchParams({ epoch })}`,
      bearer
    ),

  // A PUT to a presigned S3 URL: no bearer (the URL is the credential) and no
  // camelizing. Never throws: { ok, status }, status 0 for a network failure.
  putPresigned: async (url, body, headers) => {
    try {
      const resp = await fetch(url, { method: "PUT", headers, body });
      return { ok: resp.ok, status: resp.status };
    } catch (error) {
      return { ok: false, status: 0, error };
    }
  },

  currentBearer: async () => {
    const sess = await Auth.currentSession();
    return sess.accessToken.jwtToken;
  },

  /*

  The spoken doc (TT-1270; the platform's domain/spoken/specs/doc-model.md)

  A doc keeps its source and any number of cassettes (live, replay, batch),
  each one processing of the source; bands are a cassette's items stamped on
  the source clock (`about`, `known`). The doc read says what a doc has, its
  status, failure and audio included, so nothing here works a doc's case out
  of its fields. Top-level keys are camelized (axiosExc is shallow); nested
  ones stay as the wire sends them.

  */

  // { doc, source, policies, cassettes }: the doc's meta (title, status,
  // failure), its source (state; audio with its offset on the source clock,
  // and a signed URL for the owner) and every cassette with its state.
  getSpokenDoc: async (userId, docId) => {
    return AppBackService.axiosExc("GET", `/users/${userId}/docs/${docId}/spoken`);
  },

  // The same facts for up to 100 docs the user owns or holds a share of, in
  // the order asked: { docs: [{ doc, source, cassettes }], seen }.
  getSpokenDocs: async (userId, docIds) => {
    return AppBackService.axiosExc("GET", `/users/${userId}/docs/spoken`, { doc_ids: docIds.join(",") });
  },

  // One cassette's bands: a cold page ({ from_turn, turns }) answers with a
  // `page`, a spine cassette's delta ({ since }) with a `delta`; either way
  // `lastSeq` is where the next delta asks from.
  getDocBands: async (userId, docId, csstId, queryParams) => {
    return AppBackService.axiosExc("GET", `/users/${userId}/docs/${docId}/cssts/${csstId}/bands`, queryParams);
  },

  // A new replay or batch cassette on the owner's doc, its algorithms named
  // by the catalog's names: { cassette, created, queued }.
  requestCassette: async (userId, docId, { kind, annotators, planners }) => {
    return AppBackService.axiosExc("POST", `/users/${userId}/docs/${docId}/cssts`, {}, { kind, annotators, planners });
  },

  // TT-1046 — server-built "graphic recording" scene board for the live scene
  // system. Returns { id, spec|null, upto, calls, costUsd, job|null } (axiosExc's
  // shallow camelize renames cost_usd; the nested spec/job internals pass
  // through untouched, which the scene solver's snake_case spec keys rely on).
  // `since` (board archetype): how many plan versions the caller already
  // holds — the server answers with versions[since:] + versions_total and
  // omits the spec duplicate, so a 10s poll re-downloads nothing.
  getSceneBoard: async (userId, docId, since) => {
    const res = await AppBackService.axiosExc(
      "GET",
      `/users/${userId}/docs/${docId}/scene-board`,
      since == null ? {} : { since }
    );
    return res;
  },

  // Nudge the live cassette's planner (TT-1270): one planner pass queued, or
  // a no-op while the cassette runs its planner itself. The server keeps its
  // cost guards and answers 202 with the job state either way.
  advanceSceneBoard: async (userId, docId, data = { archetype: "story" }) => {
    const res = await AppBackService.axiosExc("POST", `/users/${userId}/docs/${docId}/scene-board/advance`, {}, data);
    return res;
  },

  startYTTranscribe: async (userId, url) => {
    const res = await AppBackService.axiosExc(
      "POST",
      `/users/${userId}/docs`,
      {},
      {
        type: "yttranscribe",
        doctype: "youtube",
        url: url,
      }
    );
    return res;
  },

  startSpacesTranscribe: async (userId, url) => {
    const res = await AppBackService.axiosExc(
      "POST",
      `/users/${userId}/docs`,
      {},
      {
        type: "spacestranscribe",
        doctype: "spaces",
        url: url,
      }
    );
    return res;
  },

  uploadAudio: async (userId, title, contentType) => {
    const res = await AppBackService.axiosExc(
      "POST",
      `/users/${userId}/docs`,
      {},
      {
        doctype: "audio",
        title: title,
        contentType,
      }
    );
    return res;
  },

  completeAudioUpload: async (userId, docId, s3Key, size) => {
    const res = await AppBackService.axiosExc(
      "PUT",
      `/users/${userId}/docs/${docId}`,
      {},
      {
        s3_key: s3Key,
        size: size,
      }
    );
    return res;
  },

  /*

  Notifications

  */

  getUserNotifications: async (userId) => {
    const res = await AppBackService.axiosExc("Get", `/users/${userId}/notifs`);

    if (res.notifs) {
      res.notifs = changeCase.toCamelfromSnakeList(res.notifs);
      res.notifs.forEach((n) => {
        if (n.event) {
          n.event = changeCase.toCamelfromSnakeArr(n.event);
        }
        if (n.tap) {
          n.tap = changeCase.toCamelfromSnakeArr(n.tap);
        }
      });
    }

    return res;
  },
};

export default AppBackService;
